///
/// EarthBrowser
///
/// Phone presentation of a layer legend: the tray shows chips only on
/// phones, and tapping one opens that legend here as a bottom sheet
/// (same shell as the phone Layers panel). Which legend is open is
/// ephemeral state in dialog.legendSheet.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { LAYERS } from "@/components/controls/layers-panel/layerRegistry"
import { setLegendSheet } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { X } from "lucide-react"
import { useEffect } from "react"

export function LegendSheet() {
	const dispatch = useAppDispatch()
	const openType = useAppSelector(state => state.dialog.legendSheet)
	const visibleDatasets = useAppSelector(state => state.earthbrowser.visibleDatasets)

	const layer = openType ? LAYERS.find(l => l.visibility === openType && l.legend) : undefined
	const layerVisible = !!openType && visibleDatasets.includes(openType)
	const showing = !!layer && layerVisible

	const close = () => dispatch(setLegendSheet(null))

	// The layer was switched off underneath the sheet
	useEffect(() => {
		if (openType && !showing) dispatch(setLegendSheet(null))
	}, [openType, showing, dispatch])

	useEffect(() => {
		if (!showing) return
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation()
				dispatch(setLegendSheet(null))
			}
		}
		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [showing, dispatch])

	if (!showing || !layer) return null

	const Content = layer.legend!.content

	return (
		<>
			<div className="fixed inset-0 z-[8000] bg-black/50" onClick={close} aria-hidden="true" />
			<div
				className={cn(
					"fixed inset-x-0 bottom-0 z-[8001] flex flex-col max-h-[70vh]",
					"rounded-t-2xl bg-slate-950/90 backdrop-blur-xl border-t border-white/20 text-slate-200"
				)}
				style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
				role="dialog"
				aria-label={`${layer.name} legend`}
			>
				<div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 flex-shrink-0">
					<span className="flex items-center [&>svg]:h-4 [&>svg]:w-4">{layer.icon}</span>
					<span className="text-sm font-semibold text-white flex-1 min-w-0 truncate">{layer.name}</span>
					<button
						onClick={close}
						className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
						aria-label="Close legend"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
				<div className="flex-1 min-h-0 overflow-y-auto px-4 pt-3 pb-4 space-y-2 text-sm">
					<Content />
				</div>
			</div>
		</>
	)
}
